// English + German translations for Aethron Computing landing page.
// Four pillars: NVIDIA Blackwell HPC, WUE 0.0 liquid cooling, waste heat recovery, 100% renewable.

export const translations = {
  en: {
    lang: { switchTo: "Deutsch", label: "Language" },
    brand: { name: "Aethron", suffix: "." },
    nav: {
      platform: "Architecture",
      impact: "Impact",
      how: "How it works",
      contact: "Contact",
      cta: "Book a tour",
    },
    hero: {
      badge: "7 zero-water HPC sites live · 100% renewable",
      title1: "High-performance,",
      titleItalic: "zero-waste",
      title2: "infrastructure.",
      sub:
        "Aethron Computing runs NVIDIA Blackwell HPC on closed-loop liquid cooling and recycles every watt of waste heat — engineered for AI, digital twins, and the low-carbon grid.",
      ctaPrimary: "Request a facility tour",
      ctaSecondary: "See the architecture",
      hint: "Drag the scene to orbit · scroll to zoom · updates every 4s",
      stats: {
        gpus: "Blackwell GPUs online",
        gpusUnit: "GPUs",
        pflops: "Compute active",
        pflopsUnit: "PFLOPS",
        heat: "Heat recycled today",
        heatUnit: "kWh",
        wue: "Water usage · WUE",
        wueUnit: "L/kWh",
      },
    },
    logos: { trusted: "Powering research & AI teams at" },
    comparison: {
      overline: "Numbers vs. industry average",
      title: "Zero-waste isn't a slogan — it's measurable.",
      sub:
        "Every Aethron facility is engineered against the same three benchmarks the hyperscalers try to hide.",
      industryLabel: "Industry avg.",
      aethronLabel: "Aethron",
      metrics: [
        {
          key: "wue",
          name: "WUE",
          unit: "L/kWh",
          aethron: "0.0",
          industry: "1.8",
          caption: "Water evaporated per kWh of compute",
        },
        {
          key: "pue",
          name: "PUE",
          unit: "",
          aethron: "1.05",
          industry: "1.58",
          caption: "Power delivered ÷ IT power (lower = tighter)",
        },
        {
          key: "renewable",
          name: "Renewable match",
          unit: "",
          aethron: "100%",
          industry: "24%",
          caption: "24/7 time-matched clean electricity",
        },
      ],
    },
    platform: {
      overline: "The Architecture",
      title: "Four pillars. One zero-waste stack.",
      sub:
        "Aethron Computing is engineered from silicon to substation — a single operational fabric for the low-carbon grid.",
      hpc: {
        title: "Next-Generation HPC Architecture",
        body:
          "We leverage state-of-the-art NVIDIA Blackwell GPU architecture to drive intensive AI modeling, real-time energy forecasting, and large-scale digital twin simulations.",
        bullets: [
          "NVIDIA Blackwell GB200 NVL72 racks",
          "InfiniBand XDR 800 Gb/s fabric",
          "Sovereign-grade multi-tenant isolation",
        ],
      },
      wue: {
        title: "WUE 0.0 Sustainability",
        body:
          "Our data center infrastructure employs advanced liquid cooling technology specifically engineered for high-density GPU performance, ensuring zero water waste (WUE 0.0) while maximizing operational efficiency.",
      },
      heat: {
        title: "Waste Heat Recovery",
        body:
          "We implement intelligent heat capture systems that recycle excess energy generated by our servers, transforming thermal waste into a productive utility for our ecosystem.",
      },
      global: {
        title: "Global Standard",
        body:
          "Built for scalability, our platform serves as the operational fabric for low-carbon grids worldwide, operating on 100% renewable energy.",
      },
    },
    impact: {
      overline: "The Impact",
      title:
        "Every joule that isn't wasted is a joule the grid doesn't have to burn.",
      stats: [
        { k: "12,480", v: "Blackwell GPUs deployed" },
        { k: "WUE 0.0", v: "Water evaporated per kWh" },
        { k: "38 GWh", v: "Heat exported to district in 2025" },
        { k: "100%", v: "Renewable-matched, 24/7" },
      ],
    },
    how: {
      overline: "How it works",
      title: "From renewable electron to productive heat.",
      steps: [
        {
          n: "01",
          t: "Renewable in",
          d:
            "Contracted wind, solar, and hydro feed each Aethron facility with 24/7 time-matched renewable electricity — verified on-chain and grid-metered.",
        },
        {
          n: "02",
          t: "Blackwell compute",
          d:
            "NVIDIA Blackwell GPU racks run AI training, inference, forecasting, and digital-twin workloads at extreme density under direct-to-chip liquid cooling.",
        },
        {
          n: "03",
          t: "Heat & data out",
          d:
            "Recovered heat is piped into district networks and industrial partners; compute results flow back to researchers, grid operators, and energy markets.",
        },
      ],
    },
    contact: {
      overline: "Let's talk",
      title: "Deploy your workload on zero-waste HPC.",
      sub:
        "Pick a 30-minute slot with an Aethron engineer. We'll walk through a live Blackwell hall, the cooling loop, and the heat-export interconnect for your workload.",
      badge1: "ISO 50001 · energy-managed",
      badge2: "ISO 27001 · SOC 2 Type II",
      fields: {
        name: "Name",
        namePh: "Ada Lovelace",
        email: "Work email",
        emailPh: "ada@company.com",
        company: "Organisation",
        companyPh: "Northwind Research",
        interest: "Interested in",
        note: "Anything we should prep? (optional)",
        notePh: "e.g. 4k H100 → Blackwell migration path",
      },
      options: {
        demo: "Facility tour",
        wind: "AI / LLM training capacity",
        solar: "Digital twin simulation",
        storage: "Heat-export partnership",
      },
      slot: {
        heading: "Pick a slot",
        subheading: "30 min · Central European Time · live availability",
        pickDate: "Choose a day",
        pickTime: "Choose a time",
        noSlots: "No availability in the next 14 business days — please email us instead.",
        taken: "Taken",
        loading: "Loading availability...",
        selected: "Selected",
      },
      submit: "Confirm my slot",
      submitting: "Booking...",
      success: "Booked. A calendar invite from an Aethron engineer will hit your inbox within 1 business day.",
      errorGeneric: "Something went wrong. Please try again.",
      errorRequired: "Name, email, and a time slot are required.",
      errorTaken: "That slot was just booked. Please choose another.",
    },
    footer: {
      tagline:
        "High-performance, zero-waste infrastructure for AI and the low-carbon grid. Headquartered in Reykjavík, deployed globally.",
      product: "Platform",
      company: "Company",
      productLinks: ["Blackwell HPC", "Liquid cooling", "Heat export", "Sovereign cloud"],
      companyLinks: ["About", "Careers", "Research", "Press"],
      copyright: "© 2026 Aethron Computing. All rights reserved. Proprietary technology. Unauthorized reproduction is strictly prohibited.",
      madeWith: "Powered by sunlight, wind, and water.",
    },
  },

  de: {
    lang: { switchTo: "English", label: "Sprache" },
    brand: { name: "Aethron", suffix: "." },
    nav: {
      platform: "Architektur",
      impact: "Wirkung",
      how: "So funktioniert's",
      contact: "Kontakt",
      cta: "Tour buchen",
    },
    hero: {
      badge: "7 wasserfreie HPC-Standorte live · 100 % erneuerbar",
      title1: "Hochleistung,",
      titleItalic: "abfallfrei",
      title2: "als Infrastruktur.",
      sub:
        "Aethron Computing betreibt NVIDIA-Blackwell-HPC mit geschlossenem Flüssigkeitskühlkreislauf und recycelt jedes Watt Abwärme — entwickelt für KI, digitale Zwillinge und das kohlenstoffarme Stromnetz.",
      ctaPrimary: "Rechenzentrums-Tour anfragen",
      ctaSecondary: "Architektur ansehen",
      hint: "Szene ziehen zum Drehen · scrollen zum Zoomen · Aktualisierung alle 4 s",
      stats: {
        gpus: "Blackwell-GPUs online",
        gpusUnit: "GPUs",
        pflops: "Rechenleistung aktiv",
        pflopsUnit: "PFLOPS",
        heat: "Heute recycelte Wärme",
        heatUnit: "kWh",
        wue: "Wasserverbrauch · WUE",
        wueUnit: "L/kWh",
      },
    },
    logos: { trusted: "Betreibt KI- und Forschungsteams bei" },
    comparison: {
      overline: "Zahlen vs. Branchendurchschnitt",
      title: "Abfallfrei ist kein Slogan — sondern messbar.",
      sub:
        "Jede Aethron-Anlage wird an denselben drei Kennzahlen gemessen, die die Hyperscaler lieber verschweigen.",
      industryLabel: "Branche Ø",
      aethronLabel: "Aethron",
      metrics: [
        {
          key: "wue",
          name: "WUE",
          unit: "L/kWh",
          aethron: "0,0",
          industry: "1,8",
          caption: "Wasserverdunstung pro kWh Rechenleistung",
        },
        {
          key: "pue",
          name: "PUE",
          unit: "",
          aethron: "1,05",
          industry: "1,58",
          caption: "Gesamtstrom ÷ IT-Strom (niedriger = effizienter)",
        },
        {
          key: "renewable",
          name: "Erneuerbar-Deckung",
          unit: "",
          aethron: "100 %",
          industry: "24 %",
          caption: "24/7 zeitlich passgenauer grüner Strom",
        },
      ],
    },
    platform: {
      overline: "Die Architektur",
      title: "Vier Säulen. Ein abfallfreier Stack.",
      sub:
        "Aethron Computing ist vom Silizium bis zum Umspannwerk konzipiert — eine einzige Betriebsschicht für das kohlenstoffarme Stromnetz.",
      hpc: {
        title: "HPC-Architektur der nächsten Generation",
        body:
          "Wir setzen auf modernste NVIDIA-Blackwell-GPU-Architektur für rechenintensive KI-Modellierung, Energieprognosen in Echtzeit und großskalige Digital-Twin-Simulationen.",
        bullets: [
          "NVIDIA Blackwell GB200 NVL72 Racks",
          "InfiniBand XDR 800 Gb/s Fabric",
          "Souveränitätskonforme Multi-Tenant-Isolation",
        ],
      },
      wue: {
        title: "WUE-0,0-Nachhaltigkeit",
        body:
          "Unsere Rechenzentrums-Infrastruktur nutzt fortschrittliche Flüssigkeitskühlung, speziell entwickelt für hochdichte GPU-Leistung — für null Wasserverbrauch (WUE 0,0) bei maximaler Betriebseffizienz.",
      },
      heat: {
        title: "Abwärmerückgewinnung",
        body:
          "Wir setzen intelligente Wärmerückgewinnungssysteme ein, die überschüssige Energie unserer Server recyceln — aus thermischem Abfall wird produktiver Nutzen für unser Ökosystem.",
      },
      global: {
        title: "Globaler Standard",
        body:
          "Skalierbar aufgebaut, dient unsere Plattform als Betriebsschicht für kohlenstoffarme Stromnetze weltweit — betrieben mit 100 % erneuerbarer Energie.",
      },
    },
    impact: {
      overline: "Die Wirkung",
      title:
        "Jedes nicht verschwendete Joule ist ein Joule, das das Netz nicht verbrennen muss.",
      stats: [
        { k: "12.480", v: "Blackwell-GPUs im Einsatz" },
        { k: "WUE 0,0", v: "Wasserverdunstung pro kWh" },
        { k: "38 GWh", v: "Fernwärme-Export 2025" },
        { k: "100 %", v: "Erneuerbar-gedeckt, 24/7" },
      ],
    },
    how: {
      overline: "So funktioniert's",
      title: "Vom erneuerbaren Elektron zur produktiven Wärme.",
      steps: [
        {
          n: "01",
          t: "Erneuerbare Energie hinein",
          d:
            "Vertraglich zugesicherter Wind-, Solar- und Wasserstrom versorgt jede Aethron-Anlage 24/7 zeitlich passgenau — verifiziert on-chain und netzgemessen.",
        },
        {
          n: "02",
          t: "Blackwell-Rechenleistung",
          d:
            "NVIDIA-Blackwell-GPU-Racks führen KI-Training, Inferenz, Prognosen und Digital-Twin-Workloads bei extremer Dichte unter Direct-to-Chip-Flüssigkeitskühlung aus.",
        },
        {
          n: "03",
          t: "Wärme & Daten hinaus",
          d:
            "Rückgewonnene Wärme fließt in Fernwärmenetze und zu Industriepartnern; Rechenergebnisse gehen zurück an Forschung, Netzbetreiber und Energiemärkte.",
        },
      ],
    },
    contact: {
      overline: "Sprechen wir",
      title: "Ihren Workload auf abfallfreier HPC betreiben.",
      sub:
        "Wählen Sie einen 30-minütigen Termin mit einem Aethron-Ingenieur. Wir führen Sie durch einen aktiven Blackwell-Saal, den Kühlkreislauf und den Wärmeexport-Anschluss für Ihren Workload.",
      badge1: "ISO 50001 · energie­gemanagt",
      badge2: "ISO 27001 · SOC 2 Typ II",
      fields: {
        name: "Name",
        namePh: "Ada Lovelace",
        email: "Geschäftliche E-Mail",
        emailPh: "ada@firma.com",
        company: "Organisation",
        companyPh: "Nordwind Forschung",
        interest: "Interessiert an",
        note: "Was sollen wir vorbereiten? (optional)",
        notePh: "z. B. 4k H100 → Blackwell Migrationspfad",
      },
      options: {
        demo: "Rechenzentrums-Tour",
        wind: "KI- / LLM-Trainingskapazität",
        solar: "Digital-Twin-Simulation",
        storage: "Wärmeexport-Partnerschaft",
      },
      slot: {
        heading: "Termin wählen",
        subheading: "30 Min · Mitteleuropäische Zeit · Echtzeit-Verfügbarkeit",
        pickDate: "Tag wählen",
        pickTime: "Uhrzeit wählen",
        noSlots: "Keine Termine in den nächsten 14 Werktagen — bitte per E-Mail melden.",
        taken: "Belegt",
        loading: "Verfügbarkeit wird geladen...",
        selected: "Ausgewählt",
      },
      submit: "Termin bestätigen",
      submitting: "Wird gebucht...",
      success: "Gebucht. Eine Kalendereinladung eines Aethron-Ingenieurs erreicht Sie innerhalb eines Werktags.",
      errorGeneric: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
      errorRequired: "Name, E-Mail und Terminwahl sind erforderlich.",
      errorTaken: "Dieser Termin wurde gerade gebucht. Bitte anderen wählen.",
    },
    footer: {
      tagline:
        "Hochleistungs- und abfallfreie Infrastruktur für KI und das kohlenstoffarme Stromnetz. Hauptsitz in Reykjavík, weltweit im Einsatz.",
      product: "Plattform",
      company: "Unternehmen",
      productLinks: ["Blackwell-HPC", "Flüssigkeitskühlung", "Wärmeexport", "Souveräne Cloud"],
      companyLinks: ["Über uns", "Karriere", "Forschung", "Presse"],
      copyright: "© 2026 Aethron Computing. Alle Rechte vorbehalten. Proprietäre Technologie. Unbefugte Vervielfältigung ist strengstens untersagt.",
      madeWith: "Angetrieben von Sonne, Wind und Wasser.",
    },
  },
};

export const supportedLocales = ["en", "de"];
